import {
  ASIAN_FUSION_CHAT_UUID,
  ASIAN_MARKET_CHAT_UUID,
  BLUE_FIN_MARKET_CHAT_UUID,
  BRONCO_DEMO_RESTAURANT_TO_CHAT,
  CENTRAL_CONFERENCE_CENTRE_CHAT_UUID,
  CHOWDER_HOUSE_CHAT_UUID,
  COASTAL_KITCHEN_SEAFOOD_CHAT_UUID,
  COWS_CREAMERY_CHAT_UUID,
  DOWNTOWN_HIGH_SCHOOL_CHAT_UUID,
  DUTCH_DREAMS_CHAT_UUID,
  FIELDSTONE_KITCHEN_CHAT_UUID,
  FINE_AND_CURED_CHAT_UUID,
  GARDEN_CAFE_CHAT_UUID,
  HOLLAND_PARK_CHAT_UUID,
  HUTCHS_ON_THE_BEACH_CHAT_UUID,
  JEFFS_GOURMET_KITCHEN_CHAT_UUID,
  KENS_CATERING_CHAT_UUID,
  LAKESHORE_DELI_CHAT_UUID,
  MALLORYS_SEAFOOD_CHAT_UUID,
  MALLORYS_STEAKHOUSE_CHAT_UUID,
  MAPLEWOOD_MART_CHAT_UUID,
  MARILUS_MARKET_CHAT_UUID,
  RAYS_CSTORE_CHAT_UUID,
  RIDGELINE_CSTORE_CHAT_UUID,
  SIAM_DISH_CHAT_UUID,
  ST_MORITZ_HOTEL_CHAT_UUID,
  SUMMIT_STEAKHOUSE_CHAT_UUID,
  THE_CARRIAGE_HOUSE_CHAT_UUID,
  THE_CARVING_BLOCK_CHAT_UUID,
  THE_DAILY_PLATE_CHAT_UUID,
  THE_PROVISIONS_COUNTER_CHAT_UUID,
  THE_REAL_SCOOP_CHAT_UUID,
  VALLEY_EXPRESS_CHAT_UUID,
  WESS_BISTRO_CHAT_UUID,
} from '@/mocks/uuids';
import { ObiItemType } from '@/types';
import type { SalesPortalAccountDetailsItemsOfInterestQuery } from '@/types';

type ObiItem =
  SalesPortalAccountDetailsItemsOfInterestQuery['getObiItems']['obi_items'][number];

/**
 * Helper to build a single OBI item matching the GraphQL query shape.
 */
const buildObiItem = (
  vpUuid: string,
  title: string,
  obiType: ObiItemType,
  times: number | undefined,
  daysAgo: number,
  photo_url_list?: string[],
): ObiItem => ({
  __typename: 'ObiItem',
  event_time: new Date(
    Date.now() - daysAgo * 24 * 60 * 60 * 1000,
  ).toISOString(),
  obi_item_type: obiType,
  subtitle: {
    __typename: 'CompleteTranslation',
    en:
      obiType === ObiItemType.ItemInCart
        ? 'Added to cart'
        : obiType === ObiItemType.ItemClickedInCatalog
          ? `Viewed${times ? ` ${times}x` : ''} from catalog`
          : `Viewed${times ? ` ${times}x` : ''} from search`,
    es:
      obiType === ObiItemType.ItemInCart
        ? 'Agregado al carrito'
        : obiType === ObiItemType.ItemClickedInCatalog
          ? `Visto${times ? ` ${times}x` : ''} desde catálogo`
          : `Visto${times ? ` ${times}x` : ''} desde búsqueda`,
    fr:
      obiType === ObiItemType.ItemInCart
        ? 'Ajouté au panier'
        : obiType === ObiItemType.ItemClickedInCatalog
          ? `Vu${times ? ` ${times}x` : ''} depuis le catalogue`
          : `Vu${times ? ` ${times}x` : ''} depuis la recherche`,
    zh:
      obiType === ObiItemType.ItemInCart
        ? '已加入购物车'
        : obiType === ObiItemType.ItemClickedInCatalog
          ? '从目录查看'
          : '从搜索查看',
  },
  title,
  variant_pack: {
    __typename: 'variants_packs',
    item: {
      __typename: 'items',
      photo_url_list,
      uuid: vpUuid,
    },
    uuid: vpUuid,
  },
  variant_pack_uuid: vpUuid,
});

/**
 * Mock OBI (Items of Interest) data for Bronco demo accounts.
 * Keyed by chat UUID → array of OBI items matching the GraphQL query shape.
 * Data sourced from the DSR-3748 demo hardcode spreadsheet.
 */
export const mockObiData: Record<string, ObiItem[]> = {
  // ==========================================================================
  // Asian Vertical
  // ==========================================================================
  [ASIAN_FUSION_CHAT_UUID]: [
    buildObiItem(
      '6a715081-a00a-4910-bf4a-57f33cdfb4b8',
      'Ohba - Shiso Leaves | 紫苏叶',
      ObiItemType.ItemInCart,
      undefined,
      1,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/b64f4917-7e28-5fee-9597-ed573f3bb7b8.jpg',
      ],
    ),
    buildObiItem(
      'd7993fbc-7cdf-424e-9a73-202f5e7f896f',
      'Shanghai Bok Choy | 上海白菜',
      ObiItemType.ItemClickedInCatalog,
      2,
      4,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/cf83bc14-3913-5e42-8a6a-4558047e69f7.jpg',
      ],
    ),
    buildObiItem(
      '53cebfaf-2b19-4f59-9180-222a871845e0',
      'Golden Burdock Root (Gobo) | 牛蒡',
      ObiItemType.ItemClickedInSearch,
      2,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/121f02df-ed91-58a6-8eb7-7ecd0753c5f8.jpg',
      ],
    ),
  ],
  [ASIAN_MARKET_CHAT_UUID]: [
    buildObiItem(
      '53cebfaf-2b19-4f59-9180-222a871845e0',
      'Golden Burdock Root (Gobo) | 牛蒡',
      ObiItemType.ItemClickedInSearch,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/121f02df-ed91-58a6-8eb7-7ecd0753c5f8.jpg',
      ],
    ),
    buildObiItem(
      'ddc870cd-9e31-4d6e-881d-1c4d3fdce835',
      'Light Green Long Bean | 白油豆',
      ObiItemType.ItemClickedInSearch,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/35a03e08-6a28-472a-82d8-93a166498128.jpeg',
      ],
    ),
    buildObiItem(
      '6a715081-a00a-4910-bf4a-57f33cdfb4b8',
      'Ohba - Shiso Leaves | 紫苏叶',
      ObiItemType.ItemInCart,
      undefined,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/b64f4917-7e28-5fee-9597-ed573f3bb7b8.jpg',
      ],
    ),
  ],

  // ==========================================================================
  // Seafood Vertical
  // ==========================================================================
  [BLUE_FIN_MARKET_CHAT_UUID]: [
    buildObiItem(
      'b52157d3-72e7-4a10-8b70-2c2c99bf14de',
      'Craft Beer Battered Cod, 10oz 1/10#',
      ObiItemType.ItemClickedInCatalog,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/802f45ea-22b6-5f70-9f64-c6027cf6aba7.jpg',
      ],
    ),
    buildObiItem(
      'ad2caa2e-10a3-4186-936a-956dd7cb8c2b',
      'Andouille Sausage Smoked',
      ObiItemType.ItemClickedInSearch,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/70c11b8a-bbce-59af-aa3a-5e3058318ed5.jpg',
      ],
    ),
    buildObiItem(
      '1a631155-2c85-4240-9ff7-99b590f42fdb',
      'Claw Crab Meat, 1 lb Can',
      ObiItemType.ItemClickedInCatalog,
      1,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/8cbedb96-01a9-5738-b4c8-993a27c96b2a.jpg',
      ],
    ),
  ],

  // ==========================================================================
  // JanSan Vertical
  // ==========================================================================
  [CENTRAL_CONFERENCE_CENTRE_CHAT_UUID]: [
    buildObiItem(
      'c54b16d0-064c-4027-ba4c-f36351d9285c',
      'BAG, SANDWICH PAPER',
      ObiItemType.ItemClickedInCatalog,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/0dbc4ff8-de24-584c-9b1b-d5bb1b6a866b.jpg',
      ],
    ),
    buildObiItem(
      'cbf7a944-f349-4083-93ed-1f02154acc8b',
      'Duracell® Optimum Batteries',
      ObiItemType.ItemClickedInCatalog,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/6655bb96-d521-5851-bcef-e0221307356a.jpg',
      ],
    ),
    buildObiItem(
      '809fbf1e-3c29-4642-82ce-9730c5df26a4',
      'Boardwalk® Urinal Deodorizer',
      ObiItemType.ItemInCart,
      undefined,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/d7ba5bb3-40c6-5f0e-ab5e-02acdc0ea90d.jpg',
      ],
    ),
  ],
  [CHOWDER_HOUSE_CHAT_UUID]: [
    buildObiItem(
      'b5484d49-28c3-4964-a625-91fd59fcfe52',
      'Kvarøy Arctic Smoked Salmon',
      ObiItemType.ItemInCart,
      undefined,
      1,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/58f14d74-4f5f-5eeb-9b64-54787a3ee6c7.png',
      ],
    ),
    buildObiItem(
      '1a631155-2c85-4240-9ff7-99b590f42fdb',
      'Claw Crab Meat, 1 lb Can',
      ObiItemType.ItemClickedInCatalog,
      1,
      4,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/8cbedb96-01a9-5738-b4c8-993a27c96b2a.jpg',
      ],
    ),
    buildObiItem(
      'ac0e2ffd-08f0-4888-bf2e-c1c020068d42',
      '❄️ Lobster Tail Meat 10#',
      ObiItemType.ItemClickedInSearch,
      2,
      6,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/2372f601-5365-52fb-8137-8de0a6e6c8c0.jpg',
      ],
    ),
  ],
  [COASTAL_KITCHEN_SEAFOOD_CHAT_UUID]: [
    buildObiItem(
      '0dc1dd53-97cc-46b8-b30d-5a7ad9765ee1',
      'P&D IQF SHRIMP 61/70 TAIL OFF',
      ObiItemType.ItemClickedInCatalog,
      2,
      3,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/3d19e467-a340-5e3e-8847-919b272bb85a.jpg',
      ],
    ),
    buildObiItem(
      'ad2caa2e-10a3-4186-936a-956dd7cb8c2b',
      'Andouille Sausage Smoked',
      ObiItemType.ItemClickedInSearch,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/70c11b8a-bbce-59af-aa3a-5e3058318ed5.jpg',
      ],
    ),
    buildObiItem(
      'b5484d49-28c3-4964-a625-91fd59fcfe52',
      'Kvarøy Arctic Smoked Salmon',
      ObiItemType.ItemInCart,
      undefined,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/58f14d74-4f5f-5eeb-9b64-54787a3ee6c7.png',
      ],
    ),
  ],

  // ==========================================================================
  // Ice Cream Vertical
  // ==========================================================================
  [COWS_CREAMERY_CHAT_UUID]: [
    buildObiItem(
      '1518b4c6-11e9-4a97-a8f7-afc7e8e9ba72',
      'Half And Half 32 Ounces',
      ObiItemType.ItemClickedInSearch,
      1,
      3,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/9b486cef-6923-5adc-bd4f-bc8d39e1e483.jpg',
      ],
    ),
    buildObiItem(
      '3e0d3cd5-4758-4c1d-856a-5d66f5c6478c',
      'Häagen Dazs Strawberry Cheesecake',
      ObiItemType.ItemClickedInCatalog,
      2,
      5,
    ),
    buildObiItem(
      'a681a22c-dfa9-4fa5-9455-55f33a9af21a',
      'Premium Chocolate Chip Ice Cream',
      ObiItemType.ItemClickedInCatalog,
      1,
      8,
    ),
  ],
  [DOWNTOWN_HIGH_SCHOOL_CHAT_UUID]: [
    buildObiItem(
      '809fbf1e-3c29-4642-82ce-9730c5df26a4',
      'Boardwalk® Urinal Deodorizer',
      ObiItemType.ItemInCart,
      undefined,
      1,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/d7ba5bb3-40c6-5f0e-ab5e-02acdc0ea90d.jpg',
      ],
    ),
    buildObiItem(
      '002ddb34-54a9-401a-a115-18cad6bc6508',
      'Zep Commercial® Fast 505 Cleaner & Degreaser',
      ObiItemType.ItemClickedInCatalog,
      2,
      4,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/2fdbf3df-ca0d-51ef-9465-8f8cbdff0e5b.jpg',
      ],
    ),
    buildObiItem(
      'c54b16d0-064c-4027-ba4c-f36351d9285c',
      'BAG, SANDWICH PAPER',
      ObiItemType.ItemClickedInCatalog,
      2,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/0dbc4ff8-de24-584c-9b1b-d5bb1b6a866b.jpg',
      ],
    ),
  ],
  [DUTCH_DREAMS_CHAT_UUID]: [
    buildObiItem(
      '2b1bb703-5690-4408-946d-a8910afa958c',
      'Vanilla Sky Bites Case',
      ObiItemType.ItemClickedInCatalog,
      2,
      2,
    ),
    buildObiItem(
      'd1a6d653-3fa6-451d-a9df-6f69f9d1e720',
      'Mint Bars 5 Count',
      ObiItemType.ItemClickedInSearch,
      2,
      4,
    ),
    buildObiItem(
      '3e0d3cd5-4758-4c1d-856a-5d66f5c6478c',
      'Häagen Dazs Strawberry Cheesecake',
      ObiItemType.ItemClickedInCatalog,
      2,
      7,
    ),
  ],

  // ==========================================================================
  // Produce Vertical
  // ==========================================================================
  [FIELDSTONE_KITCHEN_CHAT_UUID]: [
    buildObiItem(
      '9f2839a5-50ad-4e40-83a2-1b3da122bcdd',
      'Extra Long Grain Rice',
      ObiItemType.ItemClickedInCatalog,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/53f222aa-fcf0-53b7-bcf4-1cefdab5bfd5.png',
      ],
    ),
    buildObiItem(
      '85b074b6-2a6c-4f97-9b5c-d4cc8b3fb721',
      'Organic Heirloom Tomatoes',
      ObiItemType.ItemClickedInSearch,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/edef36af-ce82-5f2b-b984-4ce6257d4a25.png',
      ],
    ),
    buildObiItem(
      'db66b0db-0fcd-41c1-8b5f-c26dcee8efb7',
      'Tomato Roma Large 25#',
      ObiItemType.ItemClickedInSearch,
      2,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/fbda0d1d-9d44-502b-910c-be3b910ae7d6.png',
      ],
    ),
  ],

  // ==========================================================================
  // LP Vertical
  // ==========================================================================
  [FINE_AND_CURED_CHAT_UUID]: [
    buildObiItem(
      '2eda2de7-f2be-4b06-8340-7a27e5677a03',
      'Traditional Hummus 32 oz.',
      ObiItemType.ItemClickedInSearch,
      2,
      2,
    ),
    buildObiItem(
      'def66783-1b6c-46a5-a065-add03dbcefe6',
      'Rosina™ Meatballs',
      ObiItemType.ItemClickedInSearch,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/6c85f29c-7243-57a1-9268-bb380257c995.png',
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/971bb8d2-eb44-5fcf-990f-e62852c3c1d7.png',
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/ee3696e6-e972-5203-8341-a64463d9efe6.png',
      ],
    ),
    buildObiItem(
      '667e05f2-9eb6-49ce-98fc-0a05d2a9aaff',
      '8 Inch Philly Roll',
      ObiItemType.ItemClickedInCatalog,
      2,
      8,
    ),
  ],
  [GARDEN_CAFE_CHAT_UUID]: [
    buildObiItem(
      'db66b0db-0fcd-41c1-8b5f-c26dcee8efb7',
      'Tomato Roma Large 25#',
      ObiItemType.ItemClickedInSearch,
      2,
      3,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/fbda0d1d-9d44-502b-910c-be3b910ae7d6.png',
      ],
    ),
    buildObiItem(
      'fa085697-8f59-44c2-9048-408420ae784a',
      'Local Peaches',
      ObiItemType.ItemClickedInCatalog,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/f0795f38-c444-5010-940e-5b26b02fe108.jpg',
      ],
    ),
    buildObiItem(
      '85b074b6-2a6c-4f97-9b5c-d4cc8b3fb721',
      'Organic Heirloom Tomatoes',
      ObiItemType.ItemClickedInSearch,
      1,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/edef36af-ce82-5f2b-b984-4ce6257d4a25.png',
      ],
    ),
  ],
  [HOLLAND_PARK_CHAT_UUID]: [
    buildObiItem(
      'b0709d20-6546-4493-97b0-403ae912e9fd',
      'Apple, Honeycrisp',
      ObiItemType.ItemInCart,
      undefined,
      1,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/259918b2-c8b1-58b6-b158-39af9d342ef8.jpg',
      ],
    ),
    buildObiItem(
      'fa085697-8f59-44c2-9048-408420ae784a',
      'Local Peaches',
      ObiItemType.ItemClickedInCatalog,
      1,
      4,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/f0795f38-c444-5010-940e-5b26b02fe108.jpg',
      ],
    ),
    buildObiItem(
      'db66b0db-0fcd-41c1-8b5f-c26dcee8efb7',
      'Tomato Roma Large 25#',
      ObiItemType.ItemClickedInSearch,
      2,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/fbda0d1d-9d44-502b-910c-be3b910ae7d6.png',
      ],
    ),
  ],
  [HUTCHS_ON_THE_BEACH_CHAT_UUID]: [
    buildObiItem(
      '279e3a70-0852-4334-a323-920eff1106af',
      'Häagen Dazs Rum Raisin',
      ObiItemType.ItemInCart,
      undefined,
      1,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/3cbb787a-fb15-5533-9e80-3a4ae69e8b0b.jpg',
      ],
    ),
    buildObiItem(
      '3e0d3cd5-4758-4c1d-856a-5d66f5c6478c',
      'Häagen Dazs Strawberry Cheesecake',
      ObiItemType.ItemClickedInCatalog,
      2,
      4,
    ),
    buildObiItem(
      '1518b4c6-11e9-4a97-a8f7-afc7e8e9ba72',
      'Half And Half 32 Ounces',
      ObiItemType.ItemClickedInSearch,
      1,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/9b486cef-6923-5adc-bd4f-bc8d39e1e483.jpg',
      ],
    ),
  ],

  // ==========================================================================
  // Broadline Vertical
  // ==========================================================================
  [JEFFS_GOURMET_KITCHEN_CHAT_UUID]: [
    buildObiItem(
      'd49b19a1-1be3-4a02-9e2f-723998f942f9',
      'Potatoes, Russet',
      ObiItemType.ItemClickedInSearch,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/e21d898f-2985-5fa7-91ce-46bcd0cb2640.jpg',
      ],
    ),
    buildObiItem(
      'c6e0fc9b-885f-448e-b398-69e1523dfa79',
      '6 In Sandwich Pst Tray',
      ObiItemType.ItemClickedInCatalog,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/c591c57d-1cff-5242-be04-f019e4afad1b.jpg',
      ],
    ),
    buildObiItem(
      '7b514e1b-5e1e-4183-a53b-7930e622710e',
      'Kanpachi Portions 3oz',
      ObiItemType.ItemClickedInCatalog,
      2,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/f3ca1570-7f99-54bf-b924-1f91d59ddc47.png',
      ],
    ),
  ],
  [KENS_CATERING_CHAT_UUID]: [
    buildObiItem(
      '5eea5ca9-81c0-4b7a-968e-f92ad2d54f41',
      'Corned Beef Brisket',
      ObiItemType.ItemInCart,
      undefined,
      1,
    ),
    buildObiItem(
      '667e05f2-9eb6-49ce-98fc-0a05d2a9aaff',
      '8 Inch Philly Roll',
      ObiItemType.ItemClickedInCatalog,
      2,
      4,
    ),
    buildObiItem(
      '2eda2de7-f2be-4b06-8340-7a27e5677a03',
      'Traditional Hummus 32 oz.',
      ObiItemType.ItemClickedInSearch,
      2,
      7,
    ),
  ],

  // ==========================================================================
  // Meat Vertical
  // ==========================================================================
  [LAKESHORE_DELI_CHAT_UUID]: [
    buildObiItem(
      '570c6a6e-e4a8-4158-ad68-45b2dc73f4bd',
      'Australian Wagyu Tenderloin',
      ObiItemType.ItemClickedInSearch,
      1,
      3,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/7268d772-6d74-54b6-9653-06df772393c1.jpg',
      ],
    ),
    buildObiItem(
      'd37220d9-6536-4dac-ab9f-608098e91618',
      'Duck Breast Boneless',
      ObiItemType.ItemClickedInSearch,
      2,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/9dabeaf9-2fee-5ef0-ae7e-4be0b7047566.jpg',
      ],
    ),
    buildObiItem(
      '8e0e8cba-06e3-49c7-86ce-9fee315556b0',
      'Australian Wagyu 8-9 Grade',
      ObiItemType.ItemClickedInCatalog,
      2,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/c85630d6-9027-5d27-9715-fca0cb1f426d.jpg',
      ],
    ),
  ],
  [MALLORYS_SEAFOOD_CHAT_UUID]: [
    buildObiItem(
      'ad2caa2e-10a3-4186-936a-956dd7cb8c2b',
      'Andouille Sausage Smoked',
      ObiItemType.ItemClickedInSearch,
      1,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/70c11b8a-bbce-59af-aa3a-5e3058318ed5.jpg',
      ],
    ),
    buildObiItem(
      'ac0e2ffd-08f0-4888-bf2e-c1c020068d42',
      '❄️ Lobster Tail Meat 10#',
      ObiItemType.ItemClickedInSearch,
      2,
      4,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/2372f601-5365-52fb-8137-8de0a6e6c8c0.jpg',
      ],
    ),
    buildObiItem(
      'b5484d49-28c3-4964-a625-91fd59fcfe52',
      'Kvarøy Arctic Smoked Salmon',
      ObiItemType.ItemInCart,
      undefined,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/58f14d74-4f5f-5eeb-9b64-54787a3ee6c7.png',
      ],
    ),
  ],
  [MALLORYS_STEAKHOUSE_CHAT_UUID]: [
    buildObiItem(
      '8e0e8cba-06e3-49c7-86ce-9fee315556b0',
      'Australian Wagyu 8-9 Grade',
      ObiItemType.ItemClickedInCatalog,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/c85630d6-9027-5d27-9715-fca0cb1f426d.jpg',
      ],
    ),
    buildObiItem(
      '570c6a6e-e4a8-4158-ad68-45b2dc73f4bd',
      'Australian Wagyu Tenderloin',
      ObiItemType.ItemClickedInSearch,
      1,
      4,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/7268d772-6d74-54b6-9653-06df772393c1.jpg',
      ],
    ),
    buildObiItem(
      'd37220d9-6536-4dac-ab9f-608098e91618',
      'Duck Breast Boneless',
      ObiItemType.ItemClickedInSearch,
      2,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/9dabeaf9-2fee-5ef0-ae7e-4be0b7047566.jpg',
      ],
    ),
  ],

  // ==========================================================================
  // C-Store Vertical
  // ==========================================================================
  [MAPLEWOOD_MART_CHAT_UUID]: [
    buildObiItem(
      '5f328afe-4378-4703-b6f4-eb4f9e7c3202',
      'Pepperoni Pizza Baked Cracker',
      ObiItemType.ItemClickedInSearch,
      1,
      2,
    ),
    buildObiItem(
      '55d09f0c-a1e6-4ff8-8dff-02f316399aa0',
      'Ranch Crunch Corn Kernels',
      ObiItemType.ItemClickedInCatalog,
      1,
      5,
    ),
    buildObiItem(
      'cf8890aa-26cd-494d-8d8c-f206b1b00f73',
      'Seneca Medium King',
      ObiItemType.ItemClickedInSearch,
      2,
      7,
    ),
  ],
  [MARILUS_MARKET_CHAT_UUID]: [
    buildObiItem(
      'a1724ae4-405d-4ae6-a540-8e07bcfe04ff',
      'Lettuce, Organic, Green Leaf',
      ObiItemType.ItemClickedInCatalog,
      2,
      3,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/2d92c834-d324-571b-98e0-9286c2b0cd34.jpg',
      ],
    ),
    buildObiItem(
      '7b514e1b-5e1e-4183-a53b-7930e622710e',
      'Kanpachi Portions 3oz',
      ObiItemType.ItemClickedInCatalog,
      2,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/f3ca1570-7f99-54bf-b924-1f91d59ddc47.png',
      ],
    ),
    buildObiItem(
      'c6e0fc9b-885f-448e-b398-69e1523dfa79',
      '6 In Sandwich Pst Tray',
      ObiItemType.ItemClickedInCatalog,
      1,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/c591c57d-1cff-5242-be04-f019e4afad1b.jpg',
      ],
    ),
  ],
  [RAYS_CSTORE_CHAT_UUID]: [
    buildObiItem(
      'cf8890aa-26cd-494d-8d8c-f206b1b00f73',
      'Seneca Medium King',
      ObiItemType.ItemClickedInSearch,
      2,
      3,
    ),
    buildObiItem(
      '42422944-9f47-42e0-8c92-471c3d2a84d2',
      'ZYN Spearmint',
      ObiItemType.ItemClickedInCatalog,
      2,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/79d07280-b10d-5d2a-a1c1-283df8290ca2.jpg',
      ],
    ),
    buildObiItem(
      '55d09f0c-a1e6-4ff8-8dff-02f316399aa0',
      'Ranch Crunch Corn Kernels',
      ObiItemType.ItemClickedInCatalog,
      1,
      8,
    ),
  ],
  [RIDGELINE_CSTORE_CHAT_UUID]: [
    buildObiItem(
      'f0a75df4-ff15-4fc8-a9d5-ea7ea2dafc47',
      'Mentos Fruit Chewy Mints',
      ObiItemType.ItemClickedInCatalog,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/0d8965e8-0d76-5ed0-bb17-00c8b6711e7e.jpg',
      ],
    ),
    buildObiItem(
      '5f328afe-4378-4703-b6f4-eb4f9e7c3202',
      'Pepperoni Pizza Baked Cracker',
      ObiItemType.ItemClickedInSearch,
      1,
      4,
    ),
    buildObiItem(
      '42422944-9f47-42e0-8c92-471c3d2a84d2',
      'ZYN Spearmint',
      ObiItemType.ItemClickedInCatalog,
      2,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/79d07280-b10d-5d2a-a1c1-283df8290ca2.jpg',
      ],
    ),
  ],
  [SIAM_DISH_CHAT_UUID]: [
    buildObiItem(
      '61ac4ba3-54a2-4713-9ef9-e301ecd4b97c',
      'King Oyster Mushroom | 杏鲍菇',
      ObiItemType.ItemClickedInCatalog,
      2,
      3,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/e80bd686-ed0e-5129-b32f-67f587887072.jpg',
      ],
    ),
    buildObiItem(
      'eaed2700-882b-4ae2-9add-028e57871f72',
      'Long Hot Pepper | 虎皮椒',
      ObiItemType.ItemClickedInCatalog,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/e3422c83-f9c3-5755-8507-cb772d25c19c.jpg',
      ],
    ),
    buildObiItem(
      '53cebfaf-2b19-4f59-9180-222a871845e0',
      'Golden Burdock Root (Gobo) | 牛蒡',
      ObiItemType.ItemClickedInSearch,
      2,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/121f02df-ed91-58a6-8eb7-7ecd0753c5f8.jpg',
      ],
    ),
  ],
  [ST_MORITZ_HOTEL_CHAT_UUID]: [
    buildObiItem(
      '731831ab-cd4a-4176-812a-33881501ff4f',
      'Zep® Acidic Toilet Bowl Cleaner',
      ObiItemType.ItemClickedInSearch,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/3f6da77f-60f2-5b5a-b76d-f7a958636a38.jpg',
      ],
    ),
    buildObiItem(
      'a4dd2dad-4652-480c-b45b-982a3a234926',
      'Liner Trash Black 60Gal -150Ct',
      ObiItemType.ItemClickedInSearch,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/f7b29dd0-0880-57bd-8246-66e92825fd98.png',
      ],
    ),
    buildObiItem(
      'cbf7a944-f349-4083-93ed-1f02154acc8b',
      'Duracell® Optimum Batteries',
      ObiItemType.ItemClickedInCatalog,
      1,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/6655bb96-d521-5851-bcef-e0221307356a.jpg',
      ],
    ),
  ],
  [SUMMIT_STEAKHOUSE_CHAT_UUID]: [
    buildObiItem(
      'e4d2ccde-f47b-41e8-b7ef-aa044331c35d',
      'Cdn Lamb Diced 1" - 1.5"',
      ObiItemType.ItemInCart,
      undefined,
      1,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/b79e1820-83a3-5034-a8c7-0bd194e1a513.jpg',
      ],
    ),
    buildObiItem(
      'f4e6f0d9-bcd8-4cd4-bc11-097f7dea5811',
      'Smoked Mini Ham',
      ObiItemType.ItemClickedInCatalog,
      1,
      4,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/0ef04a16-b811-539a-9356-255936e2a2b0.jpg',
      ],
    ),
    buildObiItem(
      '570c6a6e-e4a8-4158-ad68-45b2dc73f4bd',
      'Australian Wagyu Tenderloin',
      ObiItemType.ItemClickedInSearch,
      1,
      6,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/7268d772-6d74-54b6-9653-06df772393c1.jpg',
      ],
    ),
  ],
  [THE_CARRIAGE_HOUSE_CHAT_UUID]: [
    buildObiItem(
      '85b074b6-2a6c-4f97-9b5c-d4cc8b3fb721',
      'Organic Heirloom Tomatoes',
      ObiItemType.ItemInCart,
      undefined,
      1,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/edef36af-ce82-5f2b-b984-4ce6257d4a25.png',
      ],
    ),
    buildObiItem(
      'c6e0fc9b-885f-448e-b398-69e1523dfa79',
      '6 In Sandwich Pst Tray',
      ObiItemType.ItemClickedInCatalog,
      1,
      4,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/c591c57d-1cff-5242-be04-f019e4afad1b.jpg',
      ],
    ),
    buildObiItem(
      'd49b19a1-1be3-4a02-9e2f-723998f942f9',
      'Potatoes, Russet',
      ObiItemType.ItemClickedInSearch,
      2,
      7,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/e21d898f-2985-5fa7-91ce-46bcd0cb2640.jpg',
      ],
    ),
  ],
  [THE_CARVING_BLOCK_CHAT_UUID]: [
    buildObiItem(
      'd37220d9-6536-4dac-ab9f-608098e91618',
      'Duck Breast Boneless',
      ObiItemType.ItemClickedInSearch,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/9dabeaf9-2fee-5ef0-ae7e-4be0b7047566.jpg',
      ],
    ),
    buildObiItem(
      '0783e675-4e05-4583-9faf-e7e6823d8feb',
      'Cooked Natural Turkey Breast',
      ObiItemType.ItemClickedInCatalog,
      2,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/d6866877-020b-518f-b48b-8a6c6064a2bb.jpg',
      ],
    ),
    buildObiItem(
      'e4d2ccde-f47b-41e8-b7ef-aa044331c35d',
      'Cdn Lamb Diced 1" - 1.5"',
      ObiItemType.ItemInCart,
      undefined,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/b79e1820-83a3-5034-a8c7-0bd194e1a513.jpg',
      ],
    ),
  ],
  [THE_DAILY_PLATE_CHAT_UUID]: [
    buildObiItem(
      '091237a5-a77f-4a2f-b371-3b4da16183ed',
      'Krinkle Cut Dill Slice',
      ObiItemType.ItemClickedInSearch,
      1,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/c70e754d-bf5e-5c23-92f6-3daeb25926af.jpg',
      ],
    ),
    buildObiItem(
      '85b074b6-2a6c-4f97-9b5c-d4cc8b3fb721',
      'Organic Heirloom Tomatoes',
      ObiItemType.ItemInCart,
      undefined,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/edef36af-ce82-5f2b-b984-4ce6257d4a25.png',
      ],
    ),
    buildObiItem(
      'd49b19a1-1be3-4a02-9e2f-723998f942f9',
      'Potatoes, Russet',
      ObiItemType.ItemClickedInSearch,
      2,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/e21d898f-2985-5fa7-91ce-46bcd0cb2640.jpg',
      ],
    ),
  ],
  [THE_PROVISIONS_COUNTER_CHAT_UUID]: [
    buildObiItem(
      '20128c2d-c1f5-4b15-b72a-dfb12f1a0397',
      'Hickory Smoked Beef Bacon',
      ObiItemType.ItemClickedInCatalog,
      2,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/ea6e7bcf-68bd-550a-a9a7-b376647b7b23.png',
      ],
    ),
    buildObiItem(
      '9bf02a37-fa4a-4b87-bb99-57263e0da405',
      'Aladdin Bakery™ Tomato Wraps',
      ObiItemType.ItemClickedInCatalog,
      1,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/0ec4d827-142e-5d2b-acdf-d942c67972a4.png',
      ],
    ),
    buildObiItem(
      '5eea5ca9-81c0-4b7a-968e-f92ad2d54f41',
      'Corned Beef Brisket',
      ObiItemType.ItemInCart,
      undefined,
      8,
    ),
  ],
  [THE_REAL_SCOOP_CHAT_UUID]: [
    buildObiItem(
      'd1a6d653-3fa6-451d-a9df-6f69f9d1e720',
      'Mint Bars 5 Count',
      ObiItemType.ItemClickedInSearch,
      2,
      2,
    ),
    buildObiItem(
      'a681a22c-dfa9-4fa5-9455-55f33a9af21a',
      'Premium Chocolate Chip Ice Cream',
      ObiItemType.ItemClickedInCatalog,
      1,
      5,
    ),
    buildObiItem(
      '1518b4c6-11e9-4a97-a8f7-afc7e8e9ba72',
      'Half And Half 32 Ounces',
      ObiItemType.ItemClickedInSearch,
      1,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/9b486cef-6923-5adc-bd4f-bc8d39e1e483.jpg',
      ],
    ),
  ],
  [VALLEY_EXPRESS_CHAT_UUID]: [
    buildObiItem(
      'c09e7366-a4c5-4bda-8e01-81dad70c9224',
      'Cherry Nibs',
      ObiItemType.ItemInCart,
      undefined,
      1,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/860b52e4-34f7-56f2-848b-94006e8121eb.jpg',
      ],
    ),
    buildObiItem(
      '55d09f0c-a1e6-4ff8-8dff-02f316399aa0',
      'Ranch Crunch Corn Kernels',
      ObiItemType.ItemClickedInCatalog,
      1,
      4,
    ),
    buildObiItem(
      '5f328afe-4378-4703-b6f4-eb4f9e7c3202',
      'Pepperoni Pizza Baked Cracker',
      ObiItemType.ItemClickedInSearch,
      1,
      7,
    ),
  ],
  [WESS_BISTRO_CHAT_UUID]: [
    buildObiItem(
      '85b074b6-2a6c-4f97-9b5c-d4cc8b3fb721',
      'Organic Heirloom Tomatoes',
      ObiItemType.ItemClickedInSearch,
      1,
      2,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/edef36af-ce82-5f2b-b984-4ce6257d4a25.png',
      ],
    ),
    buildObiItem(
      'b0709d20-6546-4493-97b0-403ae912e9fd',
      'Apple, Honeycrisp',
      ObiItemType.ItemInCart,
      undefined,
      5,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/259918b2-c8b1-58b6-b158-39af9d342ef8.jpg',
      ],
    ),
    buildObiItem(
      'fa085697-8f59-44c2-9048-408420ae784a',
      'Local Peaches',
      ObiItemType.ItemClickedInCatalog,
      1,
      8,
      [
        'https://s3.amazonaws.com/media.usepepper.com/suppliers/abf2eaaa-e22c-4553-a34e-d2300b073b26/items/f0795f38-c444-5010-940e-5b26b02fe108.jpg',
      ],
    ),
  ],
};

/**
 * Returns mock OBI items for a Bronco demo restaurant, or undefined if not a demo account.
 */
export const getDemoObiForBroncoRestaurant = (
  restaurantUUID: string,
): ObiItem[] | undefined => {
  const chatUUID = BRONCO_DEMO_RESTAURANT_TO_CHAT[restaurantUUID];
  if (!chatUUID) {
    return undefined;
  }

  return mockObiData[chatUUID] ?? [];
};
